import { useState, useEffect } from 'react';
import { Link, useLocation, Outlet, useNavigate } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import {
  LayoutDashboard, Users, Package, ShoppingCart, CreditCard,
  MapPin, BarChart3, Settings, Menu, X, LogOut, ChevronRight, Timer, UserCog, RotateCcw, Gift, DownloadCloud
} from 'lucide-react';
// ShoppingCart kept for nav icon (أوامر البيع)
import SellGoLogo from '@/components/SellGoLogo';
import OfflineBanner from '@/components/OfflineBanner';
import { useAuth } from '@/lib/AuthContext';
import { useOdooAuth } from '@/lib/OdooAuthContext';
import { useRepProfile } from '@/lib/RepProfileContext';
import { Toaster } from 'sonner';
import { toast } from 'sonner';
import { prepareOffline } from '@/lib/prepareOffline';
import { getActiveVisit } from '@/lib/useActiveVisit';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel,
  AlertDialogContent, AlertDialogDescription, AlertDialogFooter,
  AlertDialogHeader, AlertDialogTitle
} from '@/components/ui/alert-dialog';

// Each nav item carries a stable `screenKey` matching the backend
// `sellgo.rep.profile.hidden_screens` CSV values, so an admin can hide
// the entry per-rep without us touching the layout component.
const navItems = [
  { path: '/', label: 'لوحة التحكم', icon: LayoutDashboard, screenKey: 'dashboard' },
  { path: '/customers', label: 'العملاء', icon: Users, screenKey: 'customers' },
  { path: '/products', label: 'المنتجات', icon: Package, screenKey: 'products' },
  { path: '/orders', label: 'أوامر البيع', icon: ShoppingCart, screenKey: 'orders' },
  { path: '/returns', label: 'المرتجعات', icon: RotateCcw, screenKey: 'returns' },
  { path: '/gifts', label: 'الهدايا', icon: Gift, screenKey: 'gifts' },
  { path: '/collections', label: 'التحصيلات', icon: CreditCard, screenKey: 'collections' },
  { path: '/visits', label: 'الزيارات', icon: MapPin, screenKey: 'visits' },
  { path: '/reports', label: 'التقارير', icon: BarChart3, screenKey: 'reports' },
  // leaderOnly: shown for managers AND supervisors (rep-monitoring screen).
  { path: '/team', label: 'متابعة المناديب', icon: UserCog, screenKey: 'team', leaderOnly: true },
  // managerOnly: shown only when odooSession.role === 'manager'.
  // Reps don't get app-level settings — admins configure them in Odoo.
  { path: '/settings', label: 'الإعدادات', icon: Settings, screenKey: 'settings', managerOnly: true },
];

// Pages where back button should NOT appear
const NO_BACK_PATHS = ['/', '/customers', '/products', '/orders', '/collections', '/visits', '/reports', '/settings'];

function ActiveVisitBanner() {
  const navigate = useNavigate();
  const [activeVisit, setActiveVisit] = useState(null);
  const [elapsed, setElapsed] = useState(0);

  useEffect(() => {
    const check = () => {
      const v = getActiveVisit();
      setActiveVisit(v && v.step >= 3 ? v : null);
    };
    check();
    // Refresh every 5s in case state changed on another page
    const poll = setInterval(check, 5000);
    return () => clearInterval(poll);
  }, []);

  useEffect(() => {
    if (!activeVisit?.activeStartTime) return;
    const tick = setInterval(() => {
      setElapsed(Math.floor((Date.now() - activeVisit.activeStartTime) / 1000));
    }, 1000);
    return () => clearInterval(tick);
  }, [activeVisit?.activeStartTime]);

  if (!activeVisit) return null;

  const mins = Math.floor(elapsed / 60);
  const secs = String(elapsed % 60).padStart(2, '0');

  return (
    <button
      onClick={() => navigate('/visits/new')}
      className="w-full bg-primary text-white px-4 py-2 flex items-center gap-2 text-sm font-semibold hover:bg-primary/90 transition-colors"
    >
      <div className="w-2 h-2 bg-white rounded-full animate-pulse flex-shrink-0" />
      <span className="flex-1 text-right truncate">زيارة جارية: {activeVisit.customer?.name}</span>
      <div className="flex items-center gap-1 flex-shrink-0">
        <Timer className="w-3.5 h-3.5 opacity-80" />
        <span className="font-mono">{mins}:{secs}</span>
      </div>
    </button>
  );
}

const PAGE_TITLES = {
  '/': 'SellGo - لوحة التحكم',
  '/customers': 'SellGo - العملاء',
  '/products': 'SellGo - المنتجات',
  '/orders': 'SellGo - أوامر البيع',
  '/collections': 'SellGo - التحصيلات',
  '/visits': 'SellGo - الزيارات',
  '/reports': 'SellGo - التقارير',
  '/settings': 'SellGo - الإعدادات',
};

export default function AppLayout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showLogoutDialog, setShowLogoutDialog] = useState(false);
  const location = useLocation();


  useEffect(() => {
    const title = PAGE_TITLES[location.pathname] || 'SellGo';
    document.title = title;
  }, [location.pathname]);
  const navigate = useNavigate();
  const { user } = useAuth();
  const { odooSession, logoutOdoo } = useOdooAuth();
  const { isScreenVisible, loading: profileLoading, profile } = useRepProfile();

  // First nav screen the user is actually allowed to see (role + hidden_screens).
  const firstVisible = navItems
    .filter(item => !item.managerOnly || odooSession?.role === 'manager')
    .filter(item => !item.leaderOnly || odooSession?.role === 'manager' || odooSession?.role === 'supervisor')
    .find(item => isScreenVisible(item.screenKey));

  // If the rep landed on the dashboard but it's hidden for them, send them to
  // the first screen they can actually use instead of an empty/forbidden page.
  useEffect(() => {
    if (profileLoading) return;
    if (location.pathname === '/' && !isScreenVisible('dashboard') && firstVisible && firstVisible.path !== '/') {
      navigate(firstVisible.path, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profileLoading, location.pathname]);

  const [preparingOffline, setPreparingOffline] = useState(false);
  const handlePrepareOffline = async () => {
    if (preparingOffline) return;
    setPreparingOffline(true);
    const t = toast.loading('جاري تحضير البيانات للعمل بدون إنترنت...');
    try {
      const { ok } = await prepareOffline();
      toast.dismiss(t);
      if (ok) toast.success('تم تحضير بياناتك للعمل بدون إنترنت ✓');
      else toast.warning('تم التحضير جزئياً — تأكد من الاتصال وأعد المحاولة');
    } catch {
      toast.dismiss(t);
      toast.error('فشل التحضير — تحقق من الإنترنت');
    } finally {
      setPreparingOffline(false);
    }
  };

  const showBack = !NO_BACK_PATHS.includes(location.pathname);
  const handleBack = () => {
    if (window.history.length > 2) navigate(-1);
    else navigate('/');
  };

  const handleLogout = () => {
    logoutOdoo();
  };

  return (
    <div className="flex h-screen bg-background overflow-hidden" dir="rtl">
      {/* Mobile overlay */}
      <AnimatePresence>
        {sidebarOpen && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 z-40 lg:hidden"
            onClick={() => setSidebarOpen(false)}
          />
        )}
      </AnimatePresence>

      {/* Sidebar / Mobile Drawer */}
      <aside
        className={`
          fixed inset-y-0 right-0 z-50 w-72 bg-sidebar flex flex-col
          lg:relative lg:w-64 lg:translate-x-0 lg:flex-shrink-0
          transition-transform duration-300 ease-in-out
          ${sidebarOpen ? 'translate-x-0' : 'translate-x-full'}
        `}
        style={{ height: '100dvh' }}
      >
            {/* Logo */}
            <div className="flex items-center justify-between p-5 border-b border-sidebar-border">
              <div className="flex items-center gap-3">
                <SellGoLogo size={40} />
                <div>
                  <p className="font-black text-base leading-tight tracking-tight">
                    <span className="text-white">Sell</span>
                    <span style={{ color: '#10B981' }}>Go</span>
                  </p>
                  <p className="text-white/40 leading-tight" style={{ fontSize: '9px', letterSpacing: '0.3px' }}>Powered by <span className="font-bold" style={{ color: '#D85A4A' }}>TECH SHARK</span></p>
                </div>
              </div>
              <button onClick={() => setSidebarOpen(false)} className="lg:hidden text-sidebar-foreground/60 hover:text-sidebar-foreground">
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* User info */}
            <div className="px-4 py-3 border-b border-sidebar-border">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 bg-sidebar-primary/20 rounded-full flex items-center justify-center overflow-hidden flex-shrink-0 relative">
                  <span className="text-sidebar-primary font-bold text-sm">
                    {odooSession?.name?.charAt(0) || user?.full_name?.charAt(0) || 'م'}
                  </span>
                  {profile?.user_avatar && (
                    <img
                      src={profile.user_avatar}
                      alt=""
                      className="absolute inset-0 w-full h-full object-cover"
                      onError={(e) => { e.currentTarget.style.display = 'none'; }}
                    />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="text-sidebar-foreground text-sm font-semibold truncate">
                      {odooSession?.name || user?.full_name || 'مستخدم'}
                    </p>
                    {/* Role badge — set from Odoo, read-only */}
                    <span
                      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold flex-shrink-0 ${
                        odooSession?.role === 'manager'
                          ? 'bg-blue-500/20 text-blue-300 border border-blue-400/30'
                          : odooSession?.role === 'supervisor'
                          ? 'bg-amber-500/20 text-amber-300 border border-amber-400/30'
                          : 'bg-emerald-500/20 text-emerald-300 border border-emerald-400/30'
                      }`}
                      title="الدور محدد من إعدادات المستخدم في Odoo"
                    >
                      <span>{odooSession?.role === 'manager' ? '📊' : odooSession?.role === 'supervisor' ? '🧭' : '👤'}</span>
                      <span>{odooSession?.role === 'manager' ? 'مدير' : odooSession?.role === 'supervisor' ? 'مشرف' : 'مندوب'}</span>
                    </span>
                  </div>
                  <p className="text-sidebar-foreground/50 text-xs truncate">
                    {odooSession?.email || ''}
                  </p>
                  {odooSession?.db && (
                    <p className="text-sidebar-foreground/40 text-[10px] truncate" title="قاعدة بيانات الأودو">
                      قاعدة البيانات: {odooSession.db}
                    </p>
                  )}
                  <p className="text-sidebar-foreground/30 text-[10px] truncate" title="رقم النسخة الشغّالة">
                    بناء: {(() => { try { const v = typeof __APP_VERSION__ !== 'undefined' ? Number(__APP_VERSION__) : 0; return v ? new Date(v).toLocaleString('ar-EG', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : 'dev'; } catch { return 'dev'; } })()}
                  </p>
                </div>
              </div>
            </div>

            {/* Nav */}
            <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto min-h-0">
              {navItems
                .filter(item => !item.managerOnly || odooSession?.role === 'manager')
                .filter(item => !item.leaderOnly || odooSession?.role === 'manager' || odooSession?.role === 'supervisor')
                .filter(item => isScreenVisible(item.screenKey))
                .map(({ path, label, icon: Icon }) => {
                const active = location.pathname === path || (path !== '/' && location.pathname.startsWith(path));
                return (
                  <Link
                    key={path} to={path}
                    onClick={() => setSidebarOpen(false)}
                    className={`flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all duration-200 group
                      ${active
                        ? 'bg-sidebar-primary text-white shadow-lg shadow-sidebar-primary/30'
                        : 'text-sidebar-foreground/70 hover:bg-sidebar-accent hover:text-sidebar-foreground'
                      }`}
                  >
                    <Icon className={`w-5 h-5 flex-shrink-0 ${active ? 'text-white' : 'text-sidebar-foreground/50 group-hover:text-sidebar-foreground'}`} />
                    <span className="text-sm font-medium">{label}</span>
                  </Link>
                );
              })}

              {/* Manager-only: Rep admin link */}
              {odooSession?.role === 'manager' && (
                <>
                  <div className="my-2 border-t border-sidebar-border/30" />
                  <Link
                    to="/admin/reps"
                    onClick={() => setSidebarOpen(false)}
                    className={`flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all duration-200 group
                      ${location.pathname.startsWith('/admin/reps')
                        ? 'bg-sidebar-primary text-white shadow-lg shadow-sidebar-primary/30'
                        : 'text-sidebar-foreground/70 hover:bg-sidebar-accent hover:text-sidebar-foreground'
                      }`}
                  >
                    <Users className={`w-5 h-5 flex-shrink-0 ${location.pathname.startsWith('/admin/reps') ? 'text-white' : 'text-sidebar-foreground/50 group-hover:text-sidebar-foreground'}`} />
                    <span className="text-sm font-medium">إدارة المناديب</span>
                  </Link>
                </>
              )}
            </nav>

            {/* Offline prep + Logout */}
            <div className="p-3 border-t border-sidebar-border flex-shrink-0 space-y-1">
              <button
                onClick={handlePrepareOffline}
                disabled={preparingOffline}
                className="flex items-center gap-3 w-full px-3 py-2.5 rounded-xl text-sidebar-foreground/70 hover:bg-sidebar-accent hover:text-sidebar-foreground transition-all disabled:opacity-60"
              >
                <DownloadCloud className={`w-5 h-5 ${preparingOffline ? 'animate-pulse' : ''}`} />
                <span className="text-sm font-medium">{preparingOffline ? 'جاري التحضير...' : 'تحضير للعمل بدون إنترنت'}</span>
              </button>
              <button
                onClick={() => setShowLogoutDialog(true)}
                className="flex items-center gap-3 w-full px-3 py-2.5 rounded-xl text-sidebar-foreground/60 hover:bg-destructive/20 hover:text-red-400 transition-all"
              >
                <LogOut className="w-5 h-5" />
                <span className="text-sm font-medium">تسجيل الخروج</span>
              </button>
            </div>
      </aside>

      {/* Logout Confirmation Dialog */}
      <AlertDialog open={showLogoutDialog} onOpenChange={setShowLogoutDialog}>
        <AlertDialogContent dir="rtl">
          <AlertDialogHeader>
            <AlertDialogTitle>تسجيل الخروج</AlertDialogTitle>
            <AlertDialogDescription>
              هل تريد تسجيل الخروج؟ سيتم مسح بيانات الجلسة الحالية.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-row-reverse gap-2">
            <AlertDialogAction
              onClick={handleLogout}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              تسجيل الخروج
            </AlertDialogAction>
            <AlertDialogCancel>إلغاء</AlertDialogCancel>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Main */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Topbar */}
        <header className="bg-card border-b border-border px-4 py-3 flex items-center justify-between flex-shrink-0">
          {/* Right side: hamburger (mobile) or back button */}
          <div className="flex items-center gap-1">
            <button
              onClick={() => setSidebarOpen(true)}
              className="lg:hidden p-2 rounded-lg hover:bg-muted transition-colors"
            >
              <Menu className="w-5 h-5" />
            </button>
            {showBack && (
              <button
                onClick={handleBack}
                className="flex items-center gap-1 p-2 rounded-lg hover:bg-muted transition-colors text-primary min-w-[44px] min-h-[44px] justify-center"
                aria-label="رجوع"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            )}
          </div>
          <div className="flex items-center gap-2 text-sm">
            <span className="font-semibold text-foreground">{navItems.find(n => n.path === location.pathname)?.label || 'SellGo'}</span>
          </div>
          <div className="w-10" />
        </header>

        {/* Active Visit Banner */}
        <ActiveVisitBanner />

        {/* Offline indicator */}
        <OfflineBanner />

        {/* Page content */}
        <main className="flex-1 overflow-y-auto">
          <Outlet />
        </main>
      </div>
      <Toaster position="top-center" richColors />
    </div>
  );
}